import * as React from "react"
import { Paper, FloatingActionButton, CircularProgress, Toggle, IconMenu, IconButton, FontIcon, MenuItem } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import AddLeaveRequestDialog from "../../components/leave_tracking/AddLeaveRequestDialog"
import autobind from "class-autobind"
import LeaveTrackingStore from "../../stores/LeaveTrackingStore"
import AccountStore from "../../stores/AccountStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import * as leaveTrackingActions from "../../actions/LeaveTrackingActions"
import { ETimeoffValidationType, ITimeoffSearch, ILeaveTrackingStateFilter, ITimeoffRequestDocument, IDateRange, ETimeoffStatus, EListCalendarViewModes, ISelectedFilterTemplateId, ITimeoffFilter, EDefaultFilterValues, IUserFilterDocument, ELoggedInUserType } from "../../models"
import * as api from "../../api"
import strings from "../../localization/strings"
import TableActionBar from "../../components/layout/TableActionBar"
import LeaveTrackingFilter from "../../components/leave_tracking/LeaveTrackingFilter"
import LeaveTrackingLiveView from "../../components/leave_tracking/list_view/LeaveTrackingLiveView"
import * as permissionHelper from "../../helpers/permissionHelper"
import AuthenticationStore from "../../stores/AuthenticationStore"
import AppStateStore from "../../stores/AppStateStore"
import * as utils from "../../helpers/utils"
import * as timeHelper from "../../helpers/timeHelper"
import * as reportAction from "../../actions/ReportAction"
import ChooseListCalendarViewMode from "../../components/common/ChooseListCalendarViewMode"
import LeaveTrackingFilterForCalendarView from "../../components/leave_tracking/LeaveTrackingFilterForCalendarView"
import { LeaveTrackingCalendarView } from "../../components/leave_tracking/calendar_view"
import moment from "moment"
import { EnumValues } from "enum-values"
import TimeoffFilter from "../../components/filter_template/timeoff/TimeoffFilter"

interface IState {
	viewMode: EListCalendarViewModes
	stateFilter: ILeaveTrackingStateFilter
	referenceDateForCalendarView: Date | string
	dateFilter: IDateRange
	openLeaveRequest: boolean
	editLeaveRequest: ITimeoffRequestDocument
	disableRequestFields: boolean,
	searchText: string
	selectedLeaveRequests: ITimeoffRequestDocument[]
	leaveRequestsSearch: ITimeoffSearch
	loading: boolean
	total: number
	offset: number
	limit: number
	globalFilterId: string
	filterDirectReportsOnly: boolean
	sortBySeniorityDate: boolean
	selectedFilterTemplateId: ISelectedFilterTemplateId
	filterTemplates: ITimeoffFilter[]
}

export default class LeaveTracking extends React.Component<null, IState> {
	constructor(props) {
		super(props)
		const storedFilterDirectReportsOnly = utils.getCookie("ShowDirectReportOnlyForLeaveTrackingPage")
		const storedSortBySeniorityDate = utils.getCookie("SortBySeniorityDateForLeaveTrackingPage")
		const storedViewModeName = utils.getCookie("ViewModeForLeaveTrackingPage")
		this.state = {
			viewMode: storedViewModeName ? EListCalendarViewModes[storedViewModeName] : EListCalendarViewModes.ListView,
			stateFilter: LeaveTrackingStore.getStateFilter(),
			referenceDateForCalendarView: moment().format(),
			dateFilter: LeaveTrackingStore.getDateFilter(),
			openLeaveRequest: false,
			selectedLeaveRequests: [],
			editLeaveRequest: null,
			disableRequestFields: false,
			searchText: "",
			leaveRequestsSearch: null,
			loading: true,
			total: 0,
			offset: LeaveTrackingStore.getOffset(),
			limit: LeaveTrackingStore.getLimit(),
			globalFilterId: HierarchySettingsStore.getGlobalLevelItemFilterId(),
			filterDirectReportsOnly: storedFilterDirectReportsOnly === "true" || (AccountStore.getSupervisorDirectReportEnabled() && !permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees)),
			sortBySeniorityDate: storedSortBySeniorityDate === "true",
			selectedFilterTemplateId: EDefaultFilterValues.NONE,
			filterTemplates: [],
		}
		autobind(this)
	}

	componentDidMount() {
		document.title = `${strings.Leave_Tracking} | ${strings.Findd_Grandd_Central}`
		LeaveTrackingStore.on("change", this.changeEvent)
		HierarchySettingsStore.on("change", this.changeGlobalFilter)
		this.getLeaveRequestsAndFilters()
	}

	componentWillUnmount() {
		LeaveTrackingStore.removeListener("change", this.changeEvent)
		HierarchySettingsStore.removeListener("change", this.changeGlobalFilter)
	}

	getLeaveRequestsAndFilters() {
		this.setState({ loading: true }, () =>
			api.getFilters(AuthenticationStore.getLoggedInUser().Id).then(res => {
				const result = res.data as IUserFilterDocument
				const selectedFilterTemplateId = result && result.SelectedTimeoffFilter ? result.SelectedTimeoffFilter : EDefaultFilterValues.NONE
				const filterTemplates = result && result.TimeoffFilters ? result.TimeoffFilters : []
				this.setState({ filterTemplates, selectedFilterTemplateId }, () => this.getLeaveRequests())
			})
		)
	}

	reloadLeaveTrackingTable() {
		this.getLeaveRequests()
	}

	changeEvent() {
		this.setState({
			stateFilter: LeaveTrackingStore.getStateFilter(),
			dateFilter: LeaveTrackingStore.getDateFilter(),
			searchText: LeaveTrackingStore.getSearchText(),
			offset: LeaveTrackingStore.getOffset(),
			limit: LeaveTrackingStore.getLimit(),
		}, () => this.getLeaveRequests())
	}

	changeGlobalFilter() {
		HierarchySettingsStore.getGlobalLevelItemFilterId() !== this.state.globalFilterId &&
			this.setState({ globalFilterId: HierarchySettingsStore.getGlobalLevelItemFilterId() }, () =>
				leaveTrackingActions.initLeaveTrackingOffsetSearch()
			)
	}

	getTimeoffFilterData(): ITimeoffFilter {
		const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId)
		let timeoffFilter: ITimeoffFilter = selectedFilterTemplate ? selectedFilterTemplate : {} as ITimeoffFilter
		const statusList: ETimeoffStatus[] = []
		Object.keys(this.state.stateFilter).forEach((key) => {
			this.state.stateFilter[key] && statusList.push(ETimeoffStatus[key])
		})
		timeoffFilter = {
			...timeoffFilter,
			StartDate: this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.StartDate : moment(this.state.referenceDateForCalendarView).utc(true).startOf("month").format(),
			EndDate: this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.EndDate : moment(this.state.referenceDateForCalendarView).utc(true).endOf("month").format(),
			Search: this.state.searchText,
			HierarchyItemId: this.state.globalFilterId,
			TimeoffStatusList: statusList,
			FilterByDirectReports: this.state.filterDirectReportsOnly,
			SortBySeniorityDate: this.state.sortBySeniorityDate
		}
		return timeoffFilter
	}

	getLeaveRequests() {
		const statusList: ETimeoffStatus[] = []
		Object.keys(this.state.stateFilter).forEach((key) => {
			this.state.stateFilter[key] && statusList.push(ETimeoffStatus[key])
		})

		if (statusList.length === 0) {
			this.setState({
				selectedLeaveRequests: [],
				editLeaveRequest: null,
				leaveRequestsSearch: {
					TotalDocumentCount: 0,
					Documents: [],
				}
			})
		}
		else {
			this.setState({
				loading: true,
				selectedLeaveRequests: [],
				editLeaveRequest: null,
			}, () => {
				api.getLeaveRequests(
					AccountStore.getCurrentAccount().Id,
					this.getTimeoffFilterData(),
					this.state.viewMode === EListCalendarViewModes.ListView ? this.state.offset / this.state.limit : null,
					this.state.viewMode === EListCalendarViewModes.ListView ? this.state.limit : null
				)
					.then((res) => this.setState({ loading: false, leaveRequestsSearch: res.data }))
			})
		}
	}

	getLeaveRequestsDataDownload() {
		const statusList: ETimeoffStatus[] = []
		Object.keys(this.state.stateFilter).forEach((key) => {
			this.state.stateFilter[key] && statusList.push(ETimeoffStatus[key])
		})
		reportAction.downloadLeaveRequestsData(
			AccountStore.getCurrentAccount().Id,
			this.getTimeoffFilterData(),
			0,
			this.state.leaveRequestsSearch.TotalDocumentCount
		)
	}

	handleAdd(leaveRequest, successMessage) {
		return new Promise<ETimeoffValidationType>(resolve =>
			api.addLeaveRequest(leaveRequest, false).then(res => {
				switch (res.data) {
					case ETimeoffValidationType.None:
						AppStateStore.setProgressSnackbarContent(successMessage)
						this.getLeaveRequests()
						resolve(ETimeoffValidationType.None)
						break
					case ETimeoffValidationType.DateRangeError:
						resolve(ETimeoffValidationType.DateRangeError)
						break
					case ETimeoffValidationType.BalanceError:
						resolve(ETimeoffValidationType.BalanceError)
						break
					case ETimeoffValidationType.DuplicatedDateError:
						resolve(ETimeoffValidationType.DuplicatedDateError)
						break
					case ETimeoffValidationType.ProhibitionEndDateError:
						resolve(ETimeoffValidationType.ProhibitionEndDateError)
						break
					case ETimeoffValidationType.MaxPunchSetLengthError:
						resolve(ETimeoffValidationType.MaxPunchSetLengthError)
						break
					case ETimeoffValidationType.PayTypeError:
						resolve(ETimeoffValidationType.PayTypeError)
						break
					case ETimeoffValidationType.EmployeeInactive:
						resolve(ETimeoffValidationType.EmployeeInactive)
						break
				}
			})
		)
	}

	update(leaveRequest, successMessage) {
		return new Promise<ETimeoffValidationType>(resolve =>
			api.updateLeaveRequest(leaveRequest, false).then(res => {
				switch (res.data) {
					case ETimeoffValidationType.None:
						AppStateStore.setProgressSnackbarContent(successMessage)
						this.getLeaveRequests()
						resolve(ETimeoffValidationType.None)
						break
					case ETimeoffValidationType.DateRangeError:
						resolve(ETimeoffValidationType.DateRangeError)
						break
					case ETimeoffValidationType.BalanceError:
						resolve(ETimeoffValidationType.BalanceError)
						break
					case ETimeoffValidationType.DuplicatedDateError:
						resolve(ETimeoffValidationType.DuplicatedDateError)
						break
					case ETimeoffValidationType.ProhibitionEndDateError:
						resolve(ETimeoffValidationType.ProhibitionEndDateError)
						break
					case ETimeoffValidationType.MaxPunchSetLengthError:
						resolve(ETimeoffValidationType.MaxPunchSetLengthError)
						break
					case ETimeoffValidationType.PayTypeError:
						resolve(ETimeoffValidationType.PayTypeError)
						break
					case ETimeoffValidationType.EmployeeInactive:
						resolve(ETimeoffValidationType.EmployeeInactive)
						break
					case ETimeoffValidationType.PermissionError:
						resolve(ETimeoffValidationType.PermissionError)
						break
				}
			})
		)
	}

	handleUpdateLeaveRequest(leaveRequest: ITimeoffRequestDocument) {
		api.checkValidateUpdateTimeoff(leaveRequest.Id).then(res =>
			res.data ?
				this.setState({ openLeaveRequest: true, editLeaveRequest: leaveRequest, disableRequestFields: false })
				:
				// AppStateStore.setProgressSnackbarContent(strings.Cannot_update_leave_request_for_locked_period_or_denied_leave_request)
				this.setState({ openLeaveRequest: true, editLeaveRequest: leaveRequest, disableRequestFields: true })
		)
	}

	handleDelete(selectedLeaveRequests: ITimeoffRequestDocument[]) {
		AppStateStore.setProgressSnackbarContent("loading")
		api.deleteLeaveRequests(selectedLeaveRequests.map(item => item.Id), false).then(res => {
			switch (res.data) {
				case "Cannot_delete_approved_leave_request":
					AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_approved_leave_request)
					break
				case "Cannot_delete_leave_request_applied_in_payroll":
					AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_leave_request_applied_in_payroll)
					break
				case "Cannot_delete_denied_leave_request":
					AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_denied_leave_request)
					break
				case "Cannot_delete_approved_leave_request_for_locked_period":
					AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_approved_leave_request_for_locked_period)
					break
				case "Leave_request_deleted_successfully":
					AppStateStore.setProgressSnackbarContent(strings.Leave_request_deleted_successfully)
					this.getLeaveRequests()
					break
			}
		})
	}

	render() {
		return (
			<div className="page-container" style={{ position: "relative" }}>
				<div className="page-container-parent">
					{permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.AddLeaveTracking) &&
						<FloatingActionButton data-intercom-target={"menu-Leave-Tracking-Btn-Add"} className="fixed-add" style={{ top: 83, right: 40, zIndex: 998 }} secondary={true} onClick={() => this.setState({ openLeaveRequest: true })}>
							<ContentAdd />
						</FloatingActionButton>
					}
					<Paper zDepth={1} className="page-paper">
						<TableActionBar
							onSearch={searchText => leaveTrackingActions.setSearchText(searchText)}
							searchText={this.state.searchText}
							rowSelectionCount={permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.DeleteLeaveTracking) ? this.state.selectedLeaveRequests.length : 0}
							singleSelectedMessage={strings.Leave_Request_Selected}
							multipleSelectedMessage={strings.x_Leave_Requests_Selected}
							reloadTable={this.reloadLeaveTrackingTable}
							exportTable={this.getLeaveRequestsDataDownload}
							title={strings.Leave_Tracking}
							onDeleteClick={() =>
								AppStateStore.setConfirmDialogContent({
									content: strings.Are_you_sure_you_want_to_delete_selected_leave_request,
									confirm: () => this.handleDelete(this.state.selectedLeaveRequests)
								})
							}
						/>
						<div style={{ width: "100%", display: "flex", marginTop: -5, marginBottom: 5, justifyContent: "space-between" }}>
							<div style={{ display: "flex", alignItems: "baseline" }}>
								<TimeoffFilter
									selectedFilterTemplateId={this.state.selectedFilterTemplateId}
									filterTemplates={this.state.filterTemplates}
									handleChangeFilterTemplates={filterTemplates => this.setState({ filterTemplates })}
									handleSelectFilterTemlate={filterId =>
										this.setState({ selectedFilterTemplateId: filterId }, () => {
											api.setSelectedTimeoffFilter(AuthenticationStore.getLoggedInUser().Id,
												(filterId === EDefaultFilterValues.NONE || filterId === EDefaultFilterValues.CREATE_NEW) ? "" : filterId as string
											)
											this.getLeaveRequests()
										})
									}
								/>
								<div style={{ marginLeft: 20 }}>
									<ChooseListCalendarViewMode
										viewMode={this.state.viewMode}
										handleChange={viewMode => {
											localStorage.setItem("findd_ViewModeForLeaveTrackingPage", EnumValues.getNameFromValue(EListCalendarViewModes, viewMode))
											this.setState({ viewMode }, () => this.getLeaveRequests())
										}}
									/>
								</div>
							</div>
							<div style={{ display: "flex", flexDirection: "column" }}>
								{AccountStore.getSupervisorDirectReportEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees) &&
									<Toggle
										data-intercom-target={"menu-Leave-Tracking-Filter-Toggle-ShowDirectReportsOnly"}
										toggled={this.state.filterDirectReportsOnly}
										onToggle={(e, value) => {
											this.setState({ filterDirectReportsOnly: value, offset: 0 }, () => this.getLeaveRequests())
											AccountStore.getSupervisorDirectReportEnabled() && localStorage.setItem("findd_ShowDirectReportOnlyForLeaveTrackingPage", value)
										}}
										labelPosition="left"
										label={strings.Show_Direct_Reports_Only}
										style={{ maxWidth: 200, paddingRight: 35 }}
									/>
								}
								{AccountStore.getHREnabled() &&
									<Toggle
										data-intercom-target={"menu-Leave-Tracking-Filter-Toggle-SortBySeniorityDate"}
										toggled={this.state.sortBySeniorityDate}
										onToggle={(e, value) => {
											this.setState({ sortBySeniorityDate: value }, () => this.getLeaveRequests())
											localStorage.setItem("findd_SortBySeniorityDateForLeaveTrackingPage", value)
										}}
										labelPosition="left"
										label={strings.Sort_By_Seniority_Date}
										style={{ maxWidth: 200, paddingRight: 35 }}
									/>
								}
							</div>
						</div>
						<IconMenu
							style={{ float: "right", marginTop: this.state.viewMode === EListCalendarViewModes.ListView ? 60 : 5 }}
							iconButtonElement={
								<IconButton tooltip={strings.Reports}>
									<FontIcon className="fa fa-file-text" style={{ fontSize: 20 }} />
								</IconButton>
							}
						>
							<MenuItem
								primaryText={strings.Accrual_Balance_Report}
								leftIcon={<FontIcon className="fa fa-file-excel-o" />}
								onTouchTap={() => {
									AppStateStore.setConfirmDialogContent({
										title: strings.Accrual_Balance_Report,
										content: strings.Are_you_sure_you_want_to_download_the_Accrual_Balance_Report,
										confirm: () => reportAction.downloadAccrualBalanceReport(AccountStore.getCurrentAccount().Id, this.getTimeoffFilterData())
									})
								}}
							/>

						</IconMenu>
						{this.state.viewMode === EListCalendarViewModes.ListView ?
							<div>
								<LeaveTrackingFilter
									stateFilter={this.state.stateFilter}
									handleUpdateStateFilter={stateFilter => leaveTrackingActions.setStateFilter(stateFilter)}
									dateFilter={this.state.dateFilter}
									handleUpdateDateFilter={dateFilter => leaveTrackingActions.setDateFilter(dateFilter)}
								/>
								<div style={{ paddingLeft: 20, paddingRight: 20, paddingBottom: 20 }}>
									{this.state.loading ?
										<div className="spinner"><CircularProgress /></div> :
										<div>
											{this.state.leaveRequestsSearch.Documents.length === 0 ?
												<div>
													<h2 style={{ textAlign: "center", marginTop: 25 }}>
														{strings.No_leave_tracking_found}
													</h2>
												</div> :
												<LeaveTrackingLiveView
													showStatuActionBar={this.state.stateFilter.Requested}
													leaveRequests={this.state.leaveRequestsSearch.Documents}
													offset={this.state.offset}
													limit={this.state.limit}
													total={this.state.leaveRequestsSearch.TotalDocumentCount}
													limitOption={LeaveTrackingStore.getLimitOption()}
													onPageClick={offset => leaveTrackingActions.setOffset(offset)}
													onLimitChange={limit => leaveTrackingActions.setLimit(limit)}
													handleUpdate={this.handleUpdateLeaveRequest}
													handleSelect={selectedLeaveRequests => this.setState({ selectedLeaveRequests })}
													readonly={false}
													updateStatusCb={this.getLeaveRequests}
												/>
											}
										</div>
									}
								</div>
							</div> :
							<div>
								<div style={{ marginTop: 10 }}>
									<LeaveTrackingFilterForCalendarView
										referenceDate={this.state.referenceDateForCalendarView}
										stateFilter={this.state.stateFilter}
										handleUpdateStateFilter={stateFilter => leaveTrackingActions.setStateFilter(stateFilter)}
										handleUpdateDateFilter={dateFilter => this.setState({ referenceDateForCalendarView: dateFilter.StartDate }, () => this.getLeaveRequests())}
									/>
								</div>
								<div style={{ padding: 20 }}>
									{!this.state.loading &&
										<LeaveTrackingCalendarView
											pageName="LeaveTracking"
											loggedInUserType={ELoggedInUserType.AccountUser}
											leaveRequests={this.state.leaveRequestsSearch.Documents}
											refereceDate={moment(this.state.referenceDateForCalendarView).toDate()}
											handleUpdate={this.handleUpdateLeaveRequest}
											updateStatusCb={this.getLeaveRequests}
											handleDelete={leaveRequest => leaveRequest.AppliedInPayroll ?
												AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_leave_request_applied_in_payroll) :
												AppStateStore.setConfirmDialogContent({
													content: strings.formatString(
														strings.Are_you_sure_you_want_to_delete_leave_request_of_employee,
														leaveRequest.EmployeeName,
														moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") === moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY") ?
															moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") :
															`${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY")} - ${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY")}`
													) as string,
													confirm: () => this.handleDelete([leaveRequest])
												})
											}
										/>
									}
								</div>
							</div>
						}
					</Paper>
				</div>
				{this.state.openLeaveRequest &&
					<AddLeaveRequestDialog
						editLeaveRequest={this.state.editLeaveRequest}
						handleClose={() => this.setState({ openLeaveRequest: false, editLeaveRequest: null, disableRequestFields: false })}
						handleAdd={this.handleAdd}
						handleUpdate={this.update}
						readonly={this.state.disableRequestFields}
					/>
				}
			</div>
		)
	}
}